import React, { useState } from 'react';
import Box from 'js/components/box/box';
import Button from 'js/components/button/button';
import Col from 'js/components/grid/column';
import Row from 'js/components/grid/row';
import Input from 'js/components/input/input';
import Label from 'js/components/label/label';
import SectionTitle from 'js/components/section-title/section-title';
import Spinner from 'js/components/spinner/spinner';
import IntegrationButton from './integration-button';
import { MAIN_INTEGRATION_NAMES, OTHER_INTEGRATION_NAMES } from '../constants';
import steps from '../enums/steps.enum';
import styles from '../quick-start.module.scss';

const IntegrationSelector = ({
    integrations,
    selectedIntegration,
    disabled,
    form,
    onSelect,
}) => {
    const validIntegrations = integrations.filter((i) => {
        if (form.is_app) {
            return [null, true].includes(i.is_app);
        }
        if (form.is_video) {
            return [null, true].includes(i.is_video);
        }

        return i.is_app !== true && i.is_video !== true;
    });

    const mainIntegrations = MAIN_INTEGRATION_NAMES.map((name) =>
        validIntegrations.find(
            (item) => item.name.toLowerCase() === name.toLowerCase(),
        ),
    ).filter((item) => item);

    const otherIntegrations = OTHER_INTEGRATION_NAMES.map((name) =>
        validIntegrations.find(
            (item) => item.name.toLowerCase() === name.toLowerCase(),
        ),
    ).filter((item) => item);

    const [showAll, setShowAll] = useState(
        otherIntegrations.includes(selectedIntegration),
    );

    const visibleIntegrations = showAll
        ? [...mainIntegrations, ...otherIntegrations]
        : mainIntegrations;

    return (
        <Label label="DSP Type">
            <Row gap="base">
                {visibleIntegrations.map((integration) => (
                    <Col key={integration.id} span={12} spanMd={4} spanSm={6}>
                        <IntegrationButton
                            label={integration.name}
                            logo={integration.logo}
                            active={integration === selectedIntegration}
                            disabled={disabled}
                            onClick={() => onSelect(integration)}
                        />
                    </Col>
                ))}
            </Row>

            {!showAll && otherIntegrations.length > 0 ? (
                <Row justifyContent="center">
                    <Col span={12} spanSm="auto">
                        <Box margin={['base', 0, 0]}>
                            <Button
                                theme="outline"
                                size="small"
                                disabled={disabled}
                                onClick={() => setShowAll(true)}
                            >
                                Show All
                            </Button>
                        </Box>
                    </Col>
                </Row>
            ) : null}
        </Label>
    );
};

const PartnerIdInput = ({
    value,
    integration,
    hasError,
    errorMessage,
    onChange,
}) => {
    const isTTD = integration.name === 'The Trade Desk';

    return (
        <Input
            value={value}
            label={isTTD ? 'Partner ID' : 'Seat ID'}
            placeholder={
                isTTD
                    ? 'Enter your Partner ID (7 characters)'
                    : 'Enter your Seat ID'
            }
            maxLength={isTTD ? 7 : 50}
            hasError={hasError}
            errorMessage={errorMessage}
            focusOnShow
            onChange={(ev) => onChange(ev.target.value)}
            onBlur={() => onChange(value.trim())}
        />
    );
};

const ChooseDspStep = ({
    form,
    integrations,
    errors,
    validating,
    onFormChange,
    onStepChange,
    onValidate,
}) => (
    <>
        <SectionTitle>Let us know where to send your targeting</SectionTitle>

        <Box margin={['large', 0, 0]}>
            <IntegrationSelector
                integrations={integrations.filter(
                    (item) =>
                        item.is_video === form.is_video ||
                        item.is_video === null,
                )}
                selectedIntegration={form.integration}
                disabled={validating}
                form={form}
                onSelect={(integration) =>
                    onFormChange({ ...form, integration, partner_id: '' })
                }
            />
        </Box>

        {form.integration && (
            <Box margin={['large', 0, 0]}>
                <PartnerIdInput
                    value={form.partner_id}
                    integration={form.integration}
                    disabled={validating}
                    hasError={!!errors.validating}
                    errorMessage={
                        <p>
                            We have detected this is not a valid DSP ID. Please
                            confirm your ID and try again
                        </p>
                    }
                    onChange={(newValue) =>
                        onFormChange({ ...form, partner_id: newValue })
                    }
                />
            </Box>
        )}

        <Box margin={['large', 0, 0]}>
            <div className={styles['wizard-buttons']}>
                <Button
                    theme="outline"
                    disabled={validating}
                    onClick={() => onStepChange(steps.BUILD_CONTEXT)}
                >
                    Previous
                </Button>
                <Button
                    disabled={
                        validating ||
                        errors.validating ||
                        !form.integration ||
                        !form.partner_id
                    }
                    onClick={async () => {
                        const isValid = await onValidate();
                        if (isValid) {
                            onStepChange(steps.ACTIVATE);
                        }
                    }}
                >
                    Continue
                    {validating && (
                        <Box padding={[0, 0, 0, 'small']}>
                            <Spinner size="small" />
                        </Box>
                    )}
                </Button>
            </div>
        </Box>
    </>
);

export default ChooseDspStep;
